import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { CACHE_MS } from '../../constants';
import type {
  AgentDebugData,
  DebugViewState,
  LocalSigsEntry,
  NavigationOrigin,
  OverviewEntry,
  TeamsAgentDebugTrajectoryPayload,
} from '../../types/debug';
import { decompressSignalPayload } from '../../utils/decompressSignal';
import { parseSigsInput, parseTrajectoryInput } from '../../utils/sigsParser';
import { getSavedToken, saveToken } from '../../utils/sigsTokenStorage';
import {
  decodeTrajectoryDeeplink,
  readTrajectoryDeeplinkValue,
} from '../../utils/trajectoryDeeplink';

export type PageView = 'overview' | 'detail' | 'trajectory';
export type OverviewTab = 'recent' | 'timeline' | 'search' | 'load';
export type RecentSortKey = 'lastViewed' | 'interactionDate';
export type LoadJsonMode = 'signal' | 'trajectory';

export interface AgentDebugDataHook {
  // substrate bearer token (fallback for non-OBO environments)
  tokenInput: string;
  setTokenInput: React.Dispatch<React.SetStateAction<string>>;
  handleTokenBlur: () => void;

  // page view
  pageView: PageView;

  // detail fetch
  cvInput: string;
  setCvInput: React.Dispatch<React.SetStateAction<string>>;
  fetchLoading: boolean;
  fetchError: string | null;
  eligibilityRequired: boolean;
  parsedData: AgentDebugData[];
  selectedIndex: number;
  navOrigin: NavigationOrigin;
  detailViewState: DebugViewState;
  shareDescription: string | null;

  // Tabs
  activeOverviewTab: OverviewTab;
  setActiveOverviewTab: (tab: OverviewTab) => void;

  // Recently Viewed Tab
  overviewEntries: LocalSigsEntry[];
  overviewLoading: boolean;
  overviewError: string | null;
  fetchOverview: () => Promise<void>;
  sortedOverviewEntries: LocalSigsEntry[];
  recentSortBy: RecentSortKey;
  recentSortDir: 'desc' | 'asc';
  handleRecentSort: (key: RecentSortKey) => void;

  // Timeline Tab
  timelineEntries: OverviewEntry[];
  timelineLoading: boolean;
  timelineError: string | null;
  fetchTimeline: (days: number, refresh?: boolean) => Promise<void>;
  calendarFilterAgents: string[];
  setCalendarFilterAgents: React.Dispatch<React.SetStateAction<string[]>>;
  calendarAgentNames: string[];
  calendarFilterPlatforms: string[];
  setCalendarFilterPlatforms: React.Dispatch<React.SetStateAction<string[]>>;
  calendarPlatformNames: string[];
  filteredTimelineEntries: OverviewEntry[];
  timelineDays: number;

  // Load from JSON Tab
  loadJsonInput: string;
  setLoadJsonInput: React.Dispatch<React.SetStateAction<string>>;
  loadJsonError: string | null;
  handleLoadJson: (jsonText: string) => void;
  loadJsonMode: LoadJsonMode;
  setLoadJsonMode: (mode: LoadJsonMode) => void;
  loadedTrajectory: TeamsAgentDebugTrajectoryPayload | null;
  trajectoryLoading: boolean;
  trajectoryError: string | null;
  handleTrajectoryBack: () => void;

  // navigation handlers
  handleSelect: (index: number) => void;
  handleBack: () => void;
  handleOverviewSelect: (cv: string) => void;

  // computed
  selectedData: AgentDebugData | undefined;
}

const OVERVIEW_TABS: OverviewTab[] = ['recent', 'timeline', 'search', 'load'];
const SIGNAL_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
const SIGNAL_AVAILABILITY_DELAY_MS = 10 * 60 * 1000;
const MISSING_CV_MESSAGE =
  'This message is missing a CV. This can be caused by the agent not being integrated with AI Studio or inconsistent handling by the agent owner. Please contact the agent owner for assistance.';
const PROACTIVE_MESSAGE =
  'AI Studio does not currently support debugging proactive messages because they are not associated with a specific user.';
const SIGNAL_NOT_READY_MESSAGE =
  'It looks like you are here early. It can take up to 10 minutes for debug data to be available after the agent interaction completes.';
const SIGNAL_NOT_FOUND_MESSAGE =
  'We could not find a signal for this interaction. It may not have been captured or may no longer be available.';

function isOverviewTab(s: string): s is OverviewTab {
  return (OVERVIEW_TABS as string[]).includes(s);
}

function normalizeOid(oid: string): string {
  const unprefixedOid = oid.replace(/^8:orgid:/i, '');
  return (unprefixedOid.split('_').at(-1) ?? unprefixedOid).toLowerCase();
}

export function useAgentDebugData(
  getToken: () => Promise<string>,
  currentUserOid = '',
): AgentDebugDataHook {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  // ---- Substrate bearer token (fallback for non-OBO environments) ----
  const [tokenInput, setTokenInput] = useState(() => getSavedToken());
  const tokenInputRef = useRef(tokenInput);
  tokenInputRef.current = tokenInput;
  const handleTokenBlur = useCallback(() => {
    saveToken(tokenInput);
  }, [tokenInput]);

  // ---- URL-derived state ----
  const cvParam = searchParams.get('cv') ?? '';
  const tabParam = searchParams.get('tab') ?? '';
  const flowParam = searchParams.get('flow') ?? '';
  const messageSentAtParam = searchParams.get('messageSentAt') ?? '';
  const senderOidParam = searchParams.get('senderOid') ?? '';
  const isTeamsDiagnosticsDeeplink =
    searchParams.has('flow') ||
    searchParams.has('messageSentAt') ||
    searchParams.has('senderOid');

  const viewParam = searchParams.get('view') ?? '';
  const trajectoryDeeplinkValue = readTrajectoryDeeplinkValue(location);

  // pageView is derived from the URL — no separate state needed
  const pageView: PageView =
    cvParam || isTeamsDiagnosticsDeeplink
      ? 'detail'
      : viewParam === 'trajectory' || trajectoryDeeplinkValue
        ? 'trajectory'
        : 'overview';

  // activeOverviewTab is derived from ?tab= when on the overview
  const activeOverviewTab: OverviewTab = isOverviewTab(tabParam)
    ? tabParam
    : 'timeline';

  const setActiveOverviewTab = useCallback(
    (tab: OverviewTab) => setSearchParams({ tab }),
    [setSearchParams],
  );

  // ---- CV input for detail view (initialized from URL on mount) ----
  const [cvInput, setCvInput] = useState(cvParam);

  // ---- Signal fetch state ----
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(() =>
    isTeamsDiagnosticsDeeplink && !cvParam ? MISSING_CV_MESSAGE : null,
  );
  const [eligibilityRequired, setEligibilityRequired] = useState(false);
  const [parsedData, setParsedData] = useState<AgentDebugData[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [navOrigin, setNavOrigin] = useState<NavigationOrigin>('from-single');
  const [detailViewState, setDetailViewState] =
    useState<DebugViewState>('input');
  const [shareDescription, setShareDescription] = useState<string | null>(null);

  // ---- Recently Viewed tab ----
  const [overviewEntries, setOverviewEntries] = useState<LocalSigsEntry[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [recentSortBy, setRecentSortBy] = useState<RecentSortKey>('lastViewed');
  const [recentSortDir, setRecentSortDir] = useState<'desc' | 'asc'>('asc');

  // ---- LLM Queries / timeline tab ----
  const [timelineEntries, setTimelineEntries] = useState<OverviewEntry[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState<string | null>(null);
  const [calendarFilterAgents, setCalendarFilterAgents] = useState<string[]>(
    [],
  );
  const [calendarFilterPlatforms, setCalendarFilterPlatforms] = useState<
    string[]
  >(['AugmentationLoop']);
  const timelineDays = 7;

  // ---- Load from JSON tab ----
  const [loadJsonInput, setLoadJsonInput] = useState('');
  const [loadJsonError, setLoadJsonError] = useState<string | null>(null);
  const [loadJsonMode, setLoadJsonModeState] = useState<LoadJsonMode>('signal');
  const [loadedTrajectory, setLoadedTrajectory] =
    useState<TeamsAgentDebugTrajectoryPayload | null>(null);
  const [trajectoryLoading, setTrajectoryLoading] = useState(false);
  const [trajectoryError, setTrajectoryError] = useState<string | null>(null);
  const setLoadJsonMode = useCallback((mode: LoadJsonMode) => {
    setLoadJsonModeState(mode);
    setLoadJsonError(null);
  }, []);
  // Guard: when true, the cv-param effect skips the remote fetch (data loaded locally)
  const loadedFromJsonRef = useRef(false);
  const fetchRequestIdRef = useRef(0);

  const overviewLastFetched = useRef(0);
  const timelineLastFetched = useRef(0);

  // ---- Fetch local signals ----
  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    setOverviewError(null);
    try {
      const res = await fetch('/api/sigs/local');
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const data = (await res.json()) as LocalSigsEntry[];
      data.sort(
        (a, b) =>
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
      );
      setOverviewEntries(data);
      overviewLastFetched.current = Date.now();
    } catch {
      setOverviewError(
        'Could not load signals. Make sure the Vite dev server is running.',
      );
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  // ---- Fetch Substrate timeline ----
  const fetchTimeline = useCallback(
    async (days: number, refresh = false) => {
      setTimelineLoading(true);
      setTimelineError(null);
      try {
        const token = await getToken();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };
        const savedToken = tokenInputRef.current;
        if (savedToken) {
          headers['X-Substrate-Authorization'] = savedToken.startsWith(
            'Bearer ',
          )
            ? savedToken
            : `Bearer ${savedToken}`;
        }
        const params = new URLSearchParams({ days: String(days) });
        if (refresh) params.set('refresh', '1');
        const res = await fetch(`/api/sigs/recent?${params.toString()}`, {
          headers,
        });
        if (res.status === 401) {
          setTimelineError('auth-failed');
          return;
        }
        if (res.status === 503) {
          setTimelineError(
            'OBO auth is not available in this environment. Set a Substrate bearer token using the ⚙️ icon to use bearer passthrough.',
          );
          return;
        }
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(`${res.status} ${body.slice(0, 200)}`);
        }
        const data = (await res.json()) as OverviewEntry[];
        setTimelineEntries(data);
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const isFetchFailed =
          msg.includes('fetch failed') || msg.includes('Failed to fetch');
        setTimelineError(
          isFetchFailed
            ? 'Could not connect to the backend API. OBO is not available in this environment — set a Substrate bearer token using the ⚙️ icon.'
            : `Could not load signals: ${msg}`,
        );
      } finally {
        timelineLastFetched.current = Date.now();
        setTimelineLoading(false);
      }
    },
    [getToken],
  );

  useEffect(() => {
    if (pageView === 'overview') {
      if (Date.now() - overviewLastFetched.current < CACHE_MS) return;
      void fetchOverview();
    }
  }, [pageView, fetchOverview]);

  useEffect(() => {
    if (pageView === 'overview' && activeOverviewTab === 'timeline') {
      if (Date.now() - timelineLastFetched.current < CACHE_MS) return;
      void fetchTimeline(timelineDays);
    }
  }, [pageView, activeOverviewTab, fetchTimeline]);

  // ---- Fetch by CV ----
  const fetchByCv = useCallback(
    async (cv: string) => {
      const trimmed = cv.trim();
      if (!trimmed) {
        setFetchError('Please enter a CV or ItemId');
        return;
      }
      const requestId = ++fetchRequestIdRef.current;
      const isCurrentRequest = () => requestId === fetchRequestIdRef.current;

      setFetchLoading(true);
      setFetchError(null);
      setEligibilityRequired(false);
      setParsedData([]);

      try {
        if (flowParam === 'proactive') {
          setFetchError(PROACTIVE_MESSAGE);
          return;
        }

        const token = await getToken();
        if (!isCurrentRequest()) return;

        // Try shared signal endpoint first (handles share links and Teams deeplinks)
        const apiBase = (import.meta.env.VITE_API_BASE_URL as string) || '/api';
        const sharedRes = await fetch(
          `${apiBase}/tools/SharedSignals/by-item?itemId=${encodeURIComponent(trimmed)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!isCurrentRequest()) return;

        if (sharedRes.status === 403) {
          setEligibilityRequired(true);
          return;
        }

        if (sharedRes.ok) {
          const body = (await sharedRes.json()) as {
            description?: string | null;
            payload: unknown;
          };
          if (!isCurrentRequest()) return;
          const description: string | null =
            typeof body.description === 'string' ? body.description : null;
          const payload: string =
            typeof body.payload === 'string'
              ? body.payload
              : JSON.stringify(body.payload);
          setShareDescription(description);
          const decompressed = await decompressSignalPayload(payload);
          if (!isCurrentRequest()) return;
          const result = parseSigsInput(decompressed);
          if (result.ok && result.data.length > 0) {
            setParsedData(result.data);
            if (result.data.length === 1) {
              setSelectedIndex(0);
              setNavOrigin('from-single');
              setDetailViewState('detail');
            } else {
              setDetailViewState('list');
            }
            return;
          }
        }

        // Fall back to Substrate proxy — no share description for non-shared signals
        setShareDescription(null);
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
        };
        const savedToken = tokenInputRef.current;
        if (savedToken) {
          headers['X-Substrate-Authorization'] = savedToken.startsWith(
            'Bearer ',
          )
            ? savedToken
            : `Bearer ${savedToken}`;
        }
        const res = await fetch(`/api/sigs/${encodeURIComponent(trimmed)}`, {
          headers,
        });
        if (!isCurrentRequest()) return;

        if (res.ok) {
          const data: unknown = await res.json();
          if (!isCurrentRequest()) return;
          const result = parseSigsInput(JSON.stringify(data));
          if (result.ok) {
            setParsedData(result.data);
            if (result.data.length === 0) {
              setDetailViewState('empty');
            } else if (result.data.length === 1) {
              setSelectedIndex(0);
              setNavOrigin('from-single');
              setDetailViewState('detail');
            } else {
              setDetailViewState('list');
            }
          } else {
            setFetchError(result.error);
          }
          return;
        }

        const sentAt = Date.parse(messageSentAtParam);
        const signalAgeMs = Date.now() - sentAt;
        if (!Number.isNaN(sentAt) && signalAgeMs > SIGNAL_RETENTION_MS) {
          setFetchError(
            'This interaction is more than 7 days old and is no longer available in AI Studio. Signals can only be retrieved for the last 7 days.',
          );
          return;
        }

        const isDifferentSender =
          senderOidParam !== '' &&
          currentUserOid !== '' &&
          normalizeOid(senderOidParam) !== normalizeOid(currentUserOid);
        const isSameSender =
          senderOidParam !== '' && currentUserOid !== '' && !isDifferentSender;
        const isReactive = flowParam === '' || flowParam === 'reactive';
        setFetchError(
          isDifferentSender
            ? `The owner (${normalizeOid(senderOidParam)}), of this signal, must share it with you before you are able to debug it.`
            : (isSameSender || senderOidParam === '') &&
                isReactive &&
                !Number.isNaN(sentAt) &&
                signalAgeMs >= 0 &&
                signalAgeMs <= SIGNAL_AVAILABILITY_DELAY_MS
              ? SIGNAL_NOT_READY_MESSAGE
              : SIGNAL_NOT_FOUND_MESSAGE,
        );
      } catch (e) {
        if (!isCurrentRequest()) return;
        setFetchError(
          `Fetch error: ${e instanceof Error ? e.message : String(e)}`,
        );
      } finally {
        if (isCurrentRequest()) {
          setFetchLoading(false);
        }
      }
    },
    [currentUserOid, flowParam, getToken, messageSentAtParam, senderOidParam],
  ); // ---- Auto-fetch whenever the cv URL param changes ----
  useEffect(() => {
    if (isTeamsDiagnosticsDeeplink && !cvParam) {
      setFetchError(MISSING_CV_MESSAGE);
      setEligibilityRequired(false);
      setParsedData([]);
      setFetchLoading(false);
    }
  }, [cvParam, isTeamsDiagnosticsDeeplink]);

  useEffect(() => {
    if (!cvParam) return;
    // Skip remote fetch if data was loaded locally from JSON
    if (loadedFromJsonRef.current) {
      loadedFromJsonRef.current = false;
      return;
    }
    setParsedData([]);
    setFetchError(null);
    setDetailViewState('input');
    void fetchByCv(cvParam);
    return () => {
      fetchRequestIdRef.current += 1;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cvParam]);

  // ---- Load a trajectory from a #trajectory= deeplink ----
  useEffect(() => {
    if (pageView !== 'trajectory' || !trajectoryDeeplinkValue) return;
    let cancelled = false;
    setTrajectoryLoading(true);
    setTrajectoryError(null);
    setLoadedTrajectory(null);
    void (async () => {
      let error: string | null = null;
      let data: TeamsAgentDebugTrajectoryPayload | null = null;
      try {
        const result = parseTrajectoryInput(
          await decodeTrajectoryDeeplink(trajectoryDeeplinkValue),
        );
        if (result.ok) data = result.data;
        else error = result.error;
      } catch (err: unknown) {
        error = err instanceof Error ? err.message : String(err);
      }
      if (cancelled) return;
      setLoadedTrajectory(data);
      setTrajectoryError(error);
      setTrajectoryLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [pageView, trajectoryDeeplinkValue]);

  // ---- Select from multi-signal list ----
  const handleSelect = useCallback(
    (index: number) => {
      setSelectedIndex(index);
      setNavOrigin('from-list');
      setDetailViewState('detail');
      const selected = parsedData[index];
      if (selected?.cv) setSearchParams({ cv: selected.cv });
    },
    [parsedData, setSearchParams],
  );

  // ---- Back within the detail view ----
  const handleBack = useCallback(() => {
    if (navOrigin === 'from-list') {
      setDetailViewState('list');
    } else {
      overviewLastFetched.current = 0; // force re-fetch so Recently Viewed reflects the signal just opened
      void navigate('/debug');
    }
  }, [navOrigin, navigate]);

  // ---- Navigate from overview to detail ----
  const handleOverviewSelect = useCallback(
    (cv: string) => {
      setCvInput(cv);
      void navigate('/debug?cv=' + encodeURIComponent(cv));
    },
    [navigate],
  );

  // ---- Load from JSON handler ----
  const handleLoadJson = useCallback(
    (jsonText: string) => {
      const trimmed = jsonText.trim();
      if (!trimmed) {
        setLoadJsonError(
          loadJsonMode === 'trajectory'
            ? 'Please paste an ExecutionTrajectory JSON payload.'
            : 'Please paste a SIGS or OCV JSON payload.',
        );
        return;
      }

      if (loadJsonMode === 'trajectory') {
        const result = parseTrajectoryInput(trimmed);
        if (!result.ok) {
          setLoadJsonError(result.error);
          return;
        }
        setLoadJsonError(null);
        setTrajectoryError(null);
        setLoadedTrajectory(result.data);
        setSearchParams({ view: 'trajectory' });
        return;
      }

      void (async () => {
        // Decompress CompressedData if still in base64 gzip format
        const decompressed = await decompressSignalPayload(trimmed);
        const result = parseSigsInput(decompressed);
        if (!result.ok) {
          setLoadJsonError(result.error);
          return;
        }
        if (result.data.length === 0) {
          setLoadJsonError('No signals found in the provided JSON.');
          return;
        }
        fetchRequestIdRef.current += 1;
        setFetchLoading(false);
        setLoadJsonError(null);
        setParsedData(result.data);
        // Safe: we checked result.data.length > 0 above
        const firstSignal = result.data[0]!;
        setCvInput(firstSignal.cv || 'loaded-json');

        if (result.data.length === 1) {
          setSelectedIndex(0);
          setNavOrigin('from-single');
          setDetailViewState('detail');
        } else {
          setDetailViewState('list');
        }

        // Navigate to detail view with a synthetic cv param so URL reflects state
        const cv = firstSignal.cv || 'loaded-json';
        loadedFromJsonRef.current = true;
        setSearchParams({ cv });
      })();
    },
    [loadJsonMode, setSearchParams],
  );

  const handleTrajectoryBack = useCallback(() => {
    void navigate('/debug?tab=load');
  }, [navigate]);

  // ---- Sort toggle for Recently Viewed ----
  const handleRecentSort = useCallback(
    (key: RecentSortKey) => {
      if (recentSortBy === key) {
        setRecentSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setRecentSortBy(key);
        setRecentSortDir('desc');
      }
    },
    [recentSortBy],
  );

  // ---- Computed values ----
  const calendarAgentNames = useMemo(() => {
    const names = new Set<string>();
    for (const e of timelineEntries) {
      if (e.agentName) names.add(e.agentName);
    }
    return [...names].sort();
  }, [timelineEntries]);

  const calendarPlatformNames = useMemo(() => {
    const names = new Set<string>();
    for (const e of timelineEntries) {
      if (e.appName) names.add(e.appName);
    }
    return [...names].sort();
  }, [timelineEntries]);

  const filteredTimelineEntries = useMemo(() => {
    let entries = timelineEntries;
    if (calendarFilterPlatforms.length > 0) {
      entries = entries.filter(
        (e) => e.appName && calendarFilterPlatforms.includes(e.appName),
      );
    }
    if (calendarFilterAgents.length > 0) {
      entries = entries.filter(
        (e) => e.agentName && calendarFilterAgents.includes(e.agentName),
      );
    }
    return entries;
  }, [timelineEntries, calendarFilterAgents, calendarFilterPlatforms]);

  const sortedOverviewEntries = useMemo(() => {
    const dir = recentSortDir === 'desc' ? -1 : 1;
    return [...overviewEntries].sort((a, b) => {
      const diff =
        recentSortBy === 'interactionDate'
          ? new Date(b.startTime ?? 0).getTime() -
            new Date(a.startTime ?? 0).getTime()
          : new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      return diff * dir;
    });
  }, [overviewEntries, recentSortBy, recentSortDir]);

  const selectedData = parsedData[selectedIndex];

  return {
    tokenInput,
    setTokenInput,
    handleTokenBlur,
    pageView,
    cvInput,
    setCvInput,
    fetchLoading,
    fetchError,
    eligibilityRequired,
    parsedData,
    selectedIndex,
    navOrigin,
    detailViewState,
    activeOverviewTab,
    setActiveOverviewTab,
    overviewEntries,
    overviewLoading,
    overviewError,
    fetchOverview,
    sortedOverviewEntries,
    recentSortBy,
    recentSortDir,
    handleRecentSort,
    timelineEntries,
    timelineLoading,
    timelineError,
    fetchTimeline,
    calendarFilterAgents,
    setCalendarFilterAgents,
    calendarAgentNames,
    calendarFilterPlatforms,
    setCalendarFilterPlatforms,
    calendarPlatformNames,
    filteredTimelineEntries,
    timelineDays,
    loadJsonInput,
    setLoadJsonInput,
    loadJsonError,
    handleLoadJson,
    loadJsonMode,
    setLoadJsonMode,
    loadedTrajectory,
    trajectoryLoading,
    trajectoryError,
    handleTrajectoryBack,
    handleSelect,
    handleBack,
    handleOverviewSelect,
    selectedData,
    shareDescription,
  };
}
